/**
 * SessionAttendancePage — Phase 5.
 *
 * Roster-style attendance marking UI for a single session.
 *
 * Wiring:
 *   - useSessionAttendance(sessionId) → roster from the backend; each row
 *     either has a saved `attendance` payload or is empty (unmarked).
 *   - Local component state mirrors the editable status / notes / remarks
 *     per row. "Save attendance" issues ONE bulk POST containing every
 *     row that differs from the server snapshot (the bulk endpoint is
 *     idempotent for unchanged rows, so we could send all of them — we
 *     filter to keep the payload small and the per-row result list
 *     focused on what the operator actually touched).
 *   - "Mark all present" sets the local status for every roster row to
 *     `present` (overwriting any previously selected statuses) without
 *     saving — the operator still clicks Save.
 *   - On save, the per-row result list drives a toast + per-row error
 *     banner. Row errors stay visible until the operator edits or clears
 *     them, which matches the spec's "per-row error display" requirement.
 */

import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Card } from '@/components/common';
import { PageContainer } from '@/components/layout';
import { ApiError } from '@core/api/client';
import {
  useMarkAttendance,
  useSessionAttendance,
  type AttendanceEntryRequest,
  type AttendanceRosterEntry,
  type AttendanceStatus,
} from '@features/attendance/api/attendance';
import { ATTENDANCE_STATUS_OPTIONS } from '@features/attendance/config/ui';

interface RowDraft {
  studentId: string;
  studentName: string | null;
  attendanceId: string | null;
  status: AttendanceStatus | '';
  notes: string;
  teacherRemarks: string;
  // Server snapshot — used to compute the "changed since load" set.
  serverStatus: AttendanceStatus | '';
  serverNotes: string;
  serverRemarks: string;
  // Last-attempt result surfaced inline.
  lastError: string | null;
}

function entryToDraft(e: AttendanceRosterEntry): RowDraft {
  const a = e.attendance;
  return {
    studentId: e.student_id,
    studentName: e.student_name ?? null,
    attendanceId: a?.id ?? null,
    status: a?.status ?? '',
    notes: a?.notes ?? '',
    teacherRemarks: a?.teacher_remarks ?? '',
    serverStatus: a?.status ?? '',
    serverNotes: a?.notes ?? '',
    serverRemarks: a?.teacher_remarks ?? '',
    lastError: null,
  };
}

export const SessionAttendancePage: React.FC = () => {
  const { id: sessionId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useSessionAttendance(sessionId);
  const markMut = useMarkAttendance(sessionId ?? '');

  const [drafts, setDrafts] = useState<RowDraft[]>([]);

  // Re-seed local state whenever the server roster changes.
  useEffect(() => {
    if (data?.entries) {
      setDrafts(data.entries.map(entryToDraft));
    }
  }, [data]);

  const dirtyCount = useMemo(
    () =>
      drafts.filter(
        (d) =>
          d.status !== d.serverStatus ||
          d.notes !== d.serverNotes ||
          d.teacherRemarks !== d.serverRemarks,
      ).length,
    [drafts],
  );

  const updateDraft = (sid: string, patch: Partial<RowDraft>) => {
    setDrafts((rows) =>
      rows.map((r) => (r.studentId === sid ? { ...r, ...patch, lastError: null } : r)),
    );
  };

  const markAllPresent = () => {
    // L1 — overwrite every row, not just empty ones. The button's label
    // promises "all present"; silently skipping already-set rows surprised
    // operators who used it to fix a half-marked roster.
    setDrafts((rows) =>
      rows.map((r) => ({ ...r, status: 'present', lastError: null })),
    );
  };

  const onSave = async () => {
    if (!sessionId) return;
    const entries: AttendanceEntryRequest[] = drafts
      .filter(
        (d) =>
          d.status !== '' &&
          (d.status !== d.serverStatus ||
            d.notes !== d.serverNotes ||
            d.teacherRemarks !== d.serverRemarks),
      )
      .map((d) => ({
        student_id: d.studentId,
        status: d.status as AttendanceStatus,
        notes: d.notes || null,
        teacher_remarks: d.teacherRemarks || null,
      }));

    if (entries.length === 0) {
      toast('No changes to save');
      return;
    }

    try {
      const res = await markMut.mutateAsync(entries);
      const failures = res.results.filter((r) => !r.success);
      const successes = res.results.length - failures.length;
      if (failures.length === 0) {
        toast.success(`Saved ${successes} row${successes === 1 ? '' : 's'}`);
      } else {
        toast.error(
          `${successes} saved · ${failures.length} failed — see rows below`,
        );
      }
      // Annotate failing rows so the operator can fix them.
      setDrafts((rows) =>
        rows.map((r) => {
          const f = failures.find((x) => x.student_id === r.studentId);
          if (!f) return r;
          return {
            ...r,
            lastError: f.error_message || f.error_code || 'Failed to save',
          };
        }),
      );
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Save failed';
      toast.error(msg);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (!sessionId) {
    return (
      <PageContainer title="Attendance" data-testid="candeza-attendance-page">
        <Card padding="lg">
          <div data-testid="candeza-attendance-no-session">No session selected.</div>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title="Attendance"
      subtitle={`Session ${sessionId}`}
      data-testid="candeza-attendance-page"
      actions={
        <>
          <Button
            variant="outline"
            data-testid="candeza-attendance-back-btn"
            onClick={() => navigate(-1)}
          >
            ← Back
          </Button>
          <Button
            variant="outline"
            data-testid="candeza-attendance-mark-all-present-btn"
            data-flow="attendance-mark-all-btn"
            onClick={markAllPresent}
            disabled={isLoading || isError || drafts.length === 0}
          >
            Mark all present
          </Button>
          <Button
            data-testid="candeza-attendance-save-btn"
            data-flow="attendance-save-btn"
            onClick={onSave}
            disabled={markMut.isPending || dirtyCount === 0}
            isLoading={markMut.isPending}
          >
            Save attendance{dirtyCount > 0 ? ` (${dirtyCount})` : ''}
          </Button>
        </>
      }
    >
      <Card padding="lg">
        {isLoading && (
          <div
            data-testid="candeza-attendance-loading"
            style={{ padding: 24, color: '#64748b' }}
          >
            Loading roster…
          </div>
        )}

        {isError && (
          <div
            data-testid="candeza-attendance-error"
            style={{ padding: 24, color: '#dc2626' }}
          >
            Failed to load roster.{' '}
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        )}

        {!isLoading && !isError && drafts.length === 0 && (
          <div
            data-testid="candeza-attendance-empty"
            style={{ padding: 24, color: '#64748b', textAlign: 'center' }}
          >
            No students are enrolled in this session yet.
          </div>
        )}

        {!isLoading && !isError && drafts.length > 0 && (
          <div data-testid="candeza-attendance-roster" data-flow="attendance-roster">
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 14,
              }}
            >
              <thead>
                <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                  <th style={{ padding: 10, borderBottom: '1px solid #e2e8f0' }}>
                    Student
                  </th>
                  <th style={{ padding: 10, borderBottom: '1px solid #e2e8f0' }}>
                    Status
                  </th>
                  <th
                    style={{
                      padding: 10,
                      borderBottom: '1px solid #e2e8f0',
                      width: '30%',
                    }}
                  >
                    Notes
                  </th>
                  <th
                    style={{
                      padding: 10,
                      borderBottom: '1px solid #e2e8f0',
                      width: '25%',
                    }}
                  >
                    Teacher remarks
                  </th>
                </tr>
              </thead>
              <tbody>
                {drafts.map((d) => (
                  <RosterRow
                    key={d.studentId}
                    draft={d}
                    onChange={(patch) => updateDraft(d.studentId, patch)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </PageContainer>
  );
};

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface RosterRowProps {
  draft: RowDraft;
  onChange: (patch: Partial<RowDraft>) => void;
}

const RosterRow: React.FC<RosterRowProps> = ({ draft, onChange }) => {
  const rowTid = `candeza-attendance-row-${draft.studentId}`;
  return (
    <tr data-testid={rowTid}>
      <td style={{ padding: 10, borderBottom: '1px solid #f1f5f9' }}>
        <div
          data-testid={`candeza-attendance-student-name-${draft.studentId}`}
          style={{ fontWeight: 600 }}
        >
          {draft.studentName || draft.studentId}
        </div>
        {draft.lastError && (
          <div
            data-testid={`candeza-attendance-row-error-${draft.studentId}`}
            style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}
          >
            {draft.lastError}
          </div>
        )}
      </td>
      <td style={{ padding: 10, borderBottom: '1px solid #f1f5f9' }}>
        <select
          data-testid={`candeza-attendance-status-${draft.studentId}`}
          data-flow="attendance-status-select"
          value={draft.status}
          onChange={(e) =>
            onChange({ status: e.target.value as AttendanceStatus | '' })
          }
          style={{
            padding: '6px 8px',
            borderRadius: 4,
            border: '1px solid #cbd5e1',
            background: 'white',
            minWidth: 120,
          }}
        >
          <option value="">—</option>
          {ATTENDANCE_STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </td>
      <td style={{ padding: 10, borderBottom: '1px solid #f1f5f9' }}>
        <input
          type="text"
          data-testid={`candeza-attendance-notes-${draft.studentId}`}
          value={draft.notes}
          onChange={(e) => onChange({ notes: e.target.value })}
          placeholder="Optional"
          style={{
            width: '100%',
            padding: '6px 8px',
            borderRadius: 4,
            border: '1px solid #cbd5e1',
          }}
        />
      </td>
      <td style={{ padding: 10, borderBottom: '1px solid #f1f5f9' }}>
        <input
          type="text"
          data-testid={`candeza-attendance-remarks-${draft.studentId}`}
          value={draft.teacherRemarks}
          onChange={(e) => onChange({ teacherRemarks: e.target.value })}
          placeholder="For report card"
          style={{
            width: '100%',
            padding: '6px 8px',
            borderRadius: 4,
            border: '1px solid #cbd5e1',
          }}
        />
      </td>
    </tr>
  );
};

